import { Box, Link, Stack, Typography } from '@mui/material';
import { numericSx } from '../../../theme/typography';
import { useCurrency } from '../../../contexts/CurrencyContext';
import { PanelCard } from './PanelCard';

export interface PipelineStageRow {
  /** Stage name (e.g. "Preparation", "Delivery"). */
  name: string;
  /** Project count in this stage. */
  count: number;
  /** The stage's value, as the server summed it; absent without `view:project:value`. */
  value?: number;
  /** The stage's projects as a share of the whole book, as the server worked it out. */
  sharePct: number;
  /** 1–8 — index into the `palette.bars[N]` token registry. */
  barIndex: 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8;
}

interface PipelineListProps {
  rows: PipelineStageRow[];
  showValues?: boolean;
  /** The server's count of the whole book; without it the link names no number. */
  totalCount?: number;
  onViewAll?: () => void;
  loading?: boolean;
}

/**
 * Project-pipeline panel for the Financials section. Renders one row per
 * stage with a soft pastel bar sized by share-of-total and an aligned £
 * value on the right (gated by `showValues` — typically the parent's
 * VIEW_PROJECT_VALUE check). Mirrors the prototype's `PipelineList`.
 */
export function PipelineList({
  rows,
  showValues = true,
  totalCount,
  onViewAll,
  loading,
}: PipelineListProps) {
  const { formatCurrency } = useCurrency();

  return (
    <PanelCard
      title="Project pipeline"
      action={
        onViewAll ? (
          <Link
            component="button"
            type="button"
            onClick={onViewAll}
            underline="hover"
            sx={{ fontSize: '0.8125rem' }}
          >
            {totalCount == null ? 'View all' : `View all ${totalCount}`}
          </Link>
        ) : undefined
      }
      data-testid="widget-project-pipeline"
    >
      {rows.length === 0 ? (
        <Typography sx={{ color: 'text.secondary', fontSize: '0.8125rem', py: 2 }}>
          {loading ? 'Loading…' : 'No active projects in the pipeline yet.'}
        </Typography>
      ) : (
        <Stack spacing={1.5}>
          {rows.map((r) => {
            return (
              <Box key={r.name}>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'baseline',
                    fontSize: '0.75rem',
                    mb: '6px',
                  }}
                >
                  <Box component="span" sx={{ color: 'text.secondary' }}>
                    {r.name}{' '}
                    <Box component="span" sx={[numericSx, { color: 'text.disabled' }]}>
                      · {r.count}
                    </Box>
                  </Box>
                  {showValues && r.value != null ? (
                    <Box
                      component="span"
                      sx={[numericSx, { color: 'text.primary', fontWeight: 600 }]}
                    >
                      {formatCurrency(r.value)}
                    </Box>
                  ) : null}
                </Box>
                <Box
                  sx={(theme) => ({
                    height: 8,
                    backgroundColor: theme.palette.surface.sunk,
                    borderRadius: 4,
                    overflow: 'hidden',
                  })}
                >
                  <Box
                    sx={(theme) => ({
                      width: `${r.sharePct}%`,
                      height: '100%',
                      backgroundColor: theme.palette.bars[r.barIndex].fill,
                      borderRadius: 4,
                    })}
                  />
                </Box>
              </Box>
            );
          })}
        </Stack>
      )}
    </PanelCard>
  );
}
